import React, { useState } from "react";
import api from "../../../../api/client";

const inputStyle = {
  width: "100%", padding: "8px 12px", border: "1px solid #d6dfed",
  borderRadius: 8, fontSize: 14, boxSizing: "border-box",
};
const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: "#4f6280", marginBottom: 4 };

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

async function readError(err) {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).error;
    } catch {
      return null;
    }
  }
  return data?.error ?? null;
}

export default function PartWiseReportModal({ onClose, onError }) {
  const thisMonth = currentMonthKey();
  const [monthFrom, setMonthFrom] = useState(thisMonth);
  const [monthTo, setMonthTo] = useState(thisMonth);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const handleDownload = async () => {
    if (monthFrom > monthTo) {
      setError("The starting month cannot be after the ending month.");
      return;
    }
    setError("");
    setDownloading(true);
    try {
      const res = await api.get("/manufacturing/reports/part-wise/export", {
        params: { monthFrom, monthTo },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `part-wise-report-${monthFrom}_to_${monthTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      const msg = (await readError(err)) || "Failed to download the part-wise report.";
      setError(msg);
      onError?.(msg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={(e) => e.target === e.currentTarget && !downloading && onClose()}
    >
      <div style={{ background: "#fff", borderRadius: 14, padding: 28, width: 460, boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 18, color: "#0f2d5c" }}>Part-wise Report</h2>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748b" }}>
          Month-by-month sales per part: one row per part, one Completed / Outstanding / Pending
          group per month. Reporting is monthly — daily ranges are not available here.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
          <div>
            <label style={labelStyle}>From Month</label>
            <input
              type="month"
              style={inputStyle}
              value={monthFrom}
              max={monthTo}
              onChange={(e) => setMonthFrom(e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>To Month</label>
            <input
              type="month"
              style={inputStyle}
              value={monthTo}
              min={monthFrom}
              onChange={(e) => setMonthTo(e.target.value)}
            />
          </div>
        </div>

        <div style={{ background: "#f8fafc", border: "1px solid #e4ebf5", borderRadius: 8, padding: "10px 14px", marginBottom: 18, fontSize: 12, color: "#64748b", lineHeight: 1.7 }}>
          <div><strong style={{ color: "#15803d" }}>Completed</strong> — parts on completed POs, counted in the month the PO was finished</div>
          <div><strong style={{ color: "#dc2626" }}>Outstanding</strong> — parts on overdue POs, counted in their delivery month</div>
          <div><strong style={{ color: "#1d4ed8" }}>Pending</strong> — parts on active POs, counted in their delivery month</div>
        </div>

        {error && <p style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</p>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={downloading}
            style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid #d6dfed", background: "#f8fafc", cursor: downloading ? "default" : "pointer", fontSize: 14 }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || !monthFrom || !monthTo}
            style={{ padding: "8px 20px", borderRadius: 8, background: "#1d4ed8", color: "#fff", border: "none", cursor: downloading ? "default" : "pointer", fontWeight: 700, fontSize: 14, opacity: downloading ? 0.7 : 1 }}
          >
            {downloading ? "Preparing…" : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
}
